import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Forms from '../../../components/Forms';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import Error from '../../../components/Error';
import {toDateOrNow, toDateOrNull} from "../../../utils/tools";
import NotesEditor from "../../../components/NotesEditor";
import ModalFormHeaderRow from "../../../components/ModalFormHeaderRow";
import AddressesEditor from "../../../components/AddressesEditor";
import PhonesEditor from "../../../components/PhonesEditor";
import EmailsEditor from "../../../components/EmailsEditor";

const CustomerModal = ({close, onSaved, customer, strictPersonDuplicateByNameSurname = false}) => {
    const {t} = useTranslation();
    const isEdit = Boolean(customer?._id);

    // --- základní pole ---
    const [type, setType] = useState(customer?.type || 'person');
    const [person, setPerson] = useState(() => ({
        name: customer?.person?.name || '',
        surname: customer?.person?.surname || '',
        birthDate: customer?.person?.birthDate || null
    }));

    const [company, setCompany] = useState(() => ({
        name: customer?.company?.name || '',
        ico: customer?.company?.ico || '',
        dic: customer?.company?.dic || '',
        vatPayer: !!customer?.company?.vatPayer
    }));

    const ensureAtLeastOne = (arr, template) => (arr && arr.length) ? arr : [template];
    const [emails, setEmails] = useState(ensureAtLeastOne(customer?.emails || [], {value: '', primary: true}));
    const [phones, setPhones] = useState(ensureAtLeastOne(customer?.phones || [], {
        value: '',
        primary: true
    }));
    const [addresses, setAddresses] = useState(customer?.addresses || [newAddress]);
    const [notes, setNotes] = useState(customer?.notes || []);

    const [openAddresses, setOpenAddresses] = useState(false);
    const [openCompany, setOpenCompany] = useState(false);

    const [risk, setRisk] = useState(() => ({
        blacklisted: !!customer?.risk?.blacklisted,
        reason: customer?.risk?.reason || ''
    }));

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const firstInputRef = useRef(null);

    useEffect(() => {
        setTimeout(() => firstInputRef.current?.focus(), 1);
    }, []);

    // --- validace ---
    const errors = useMemo(() => {
        const e = {};
        if (type === 'person') {
            if (!person.name?.trim()) e['person.name'] = t('validation.required');
            if (!person.surname?.trim()) e['person.surname'] = t('validation.required');
        } else {
            if (!company.name?.trim()) e['company.name'] = t('validation.required');
        }
        if ((emails?.filter(x => x.value?.trim()).length || 0) === 0 &&
            (phones?.filter(x => x.value?.trim()).length || 0) === 0) {
            e.contacts = t('customers.validation.oneContact');
        }
        return e;
    }, [type, person, company, emails, phones, t]);

    // --- submit ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (Object.keys(errors).length) return;

        const payload = {
            type,
            person: type === 'person' ? {
                name: person.name?.trim(),
                surname: person.surname?.trim(),
                birthDate: toDateOrNull(person.birthDate),
            } : {},
            company: type === 'company' ? {
                name: company.name?.trim(),
                ico: company.ico?.trim(),
                dic: company.dic?.trim(),
                vatPayer: !!company.vatPayer
            } : {},
            emails: (emails || []).map(e => ({value: (e.value || '').trim(), primary: !!e.primary})),
            phones: (phones || []).map(p => ({
                value: (p.value || '').trim(),
                primary: !!p.primary
            })),
            addresses: addresses || [],
            risk: {
                blacklisted: !!risk.blacklisted,
                reason: (risk.reason || '').trim()
            },
            notes: (notes || []).map(n => ({text: (n.text || '').trim(), createdAt: toDateOrNow(n.createdAt)}))
        };

        try {
            if (!isEdit && strictPersonDuplicateByNameSurname && type === 'person') {
                const existingCustomersResponse = await API.get(API_ENDPOINTS.customer.list);
                const normalizedName = normalizePersonToken(person.name);
                const normalizedSurname = normalizePersonToken(person.surname);

                const duplicateExists = (existingCustomersResponse?.data || []).some((item) => {
                    if (item?.type !== 'person') return false;
                    return normalizePersonToken(item?.person?.name) === normalizedName
                        && normalizePersonToken(item?.person?.surname) === normalizedSurname;
                });

                if (duplicateExists) {
                    setError({message: 'Zákazník se stejným jménem a příjmením už existuje.'});
                    return;
                }
            }

            setSaving(true);
            let response;
            if (isEdit) {
                response = await API.put(API_ENDPOINTS.customer.update(customer._id), payload);
            } else {
                response = await API.post(API_ENDPOINTS.customer.add, payload);
            }
            onSaved?.(response.data);
            close();
        } catch (err) {
            setError(err);
            console.error('Failed to upsert customer:', err);
        } finally {
            setSaving(false);
        }
    };

    const customerOptions = [
        {value: 'person', label: t('customers.typePerson')},
        {value: 'company', label: t('customers.typeCompany')}
    ];

    // --- render ---
    return (
        <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                <Forms.Select
                    label={t('customers.type')}
                    value={customerOptions.find(o => o.value === type)}
                    options={customerOptions}
                    onChange={v => setType(v.value)}
                    className="rounded-md col-span-2 md:col-span-1"
                />

                {type === 'person' ? (
                    <>
                        <Forms.Text
                            ref={firstInputRef}
                            required
                            label={t('customers.name')}
                            value={person.name}
                            onChange={(v) => setPerson(s => ({...s, name: v}))}
                            error={errors['person.name']}
                        />
                        <Forms.Text
                            required
                            label={t('customers.surname')}
                            value={person.surname}
                            onChange={(v) => setPerson(s => ({...s, surname: v}))}
                            error={errors['person.surname']}
                        />
                    </>
                ) : (
                    <>
                        <Forms.Text
                            ref={firstInputRef}
                            required
                            label={t('customers.companyName')}
                            value={company.name}
                            onChange={(v) => setCompany(s => ({...s, name: v}))}
                            error={errors['company.name']}
                        />
                        <div/>
                    </>
                )}

                {/* -------- Emails (header s chevronem + plus) -------- */}
                <ModalFormHeaderRow
                    title={t('customers.emails')}
                    onAdd={() => setEmails(prev => [...prev, {value: '', primary: prev.length === 0}])}
                    addAria={t('customers.addEmail')}
                />
                <div className="col-span-2">
                    <EmailsEditor items={emails} onChange={setEmails}/>
                </div>

                {/* -------- Phones (header s chevronem + plus) -------- */}
                <ModalFormHeaderRow
                    title={t('phone')}
                    onAdd={() => setPhones(prev => [...prev, {value: '', primary: prev.length === 0}])}
                    addAria={t('customers.addPhone')}
                />
                <div className="col-span-2">
                    <PhonesEditor items={phones} onChange={setPhones}/>
                </div>

                {/* -------- Notes (bez togglu – jednoduché) -------- */}
                <div className="col-span-2">
                    <SectionTitle>{t('notes')}</SectionTitle>
                    <NotesEditor items={notes} onChange={setNotes}/>
                </div>

                {/* -------- Company (header s chevronem) -------- */}
                <ModalFormHeaderRow
                    title={t('customers.company')}
                    open={openCompany}
                    onToggle={() => setOpenCompany(v => !v)}
                />
                {openCompany && (
                    <div className="col-span-2 grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Forms.Text label={t('customers.companyName')} value={company.name}
                                    onChange={v => setCompany(s => ({...s, name: v}))}/>
                        <Forms.Text label={t('customers.ico')} value={company.ico}
                                    onChange={v => setCompany(s => ({...s, ico: v}))}/>
                        <Forms.Text label={t('customers.dic')} value={company.dic}
                                    onChange={v => setCompany(s => ({...s, dic: v}))}/>
                        <Forms.Checkbox label={t('customers.vatPayer')} checked={!!company.vatPayer}
                                        onChange={v => setCompany(s => ({...s, vatPayer: v}))}/>
                    </div>
                )}

                {/* -------- Addresses (header s chevronem + plus) -------- */}
                <ModalFormHeaderRow
                    title={t('customers.addresses')}
                    open={openAddresses}
                    onToggle={() => setOpenAddresses(v => !v)}
                    onAdd={() => setAddresses(prev => [...prev, newAddress])}
                    addAria={t('customers.addAddress')}
                />
                {openAddresses && (
                    <div className="col-span-2">
                        <AddressesEditor items={addresses} onChange={setAddresses}/>
                    </div>
                )}

                {/* -------- Risk (header s chevronem) -------- */}
                <ModalFormHeaderRow
                    title={t('customers.risk')}
                />
                <div className="col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Forms.Checkbox label={t('customers.blacklisted')} checked={!!risk.blacklisted}
                                    onChange={v => setRisk(s => ({...s, blacklisted: v}))}/>
                    <Forms.Text label={t('customers.blacklistReason')} value={risk.reason}
                                onChange={v => setRisk(s => ({...s, reason: v}))}/>
                </div>
            </div>

            {error && <Error error={error}/>}

            <Forms.Button submit label={t('save')} className="mt-4" disabled={saving}/>
        </form>
    );
};

export default CustomerModal;

function normalizePersonToken(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
}

const SectionTitle = ({children}) => (
    <div className="text-sm font-semibold mb-2">{children}</div>
);

const newAddress = {
    type: 'other', label: '', company: '', name: '',
    street: '', houseNumber: '', city: '', region: '',
    zip: '', countryCode: 'CZ', isDefault: false, note: ''
};
